
































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import MultiSelect from "@/components/common/MultiSelect.vue";
import { coverageTypeOf } from "@/util/filterUtils";
import { tools } from "@/util/tools";

/**
 * Client-side sibling of ServerFilterPanel: same multi-select look, but it derives the filter
 * options from the already-loaded items and filters them in memory (no backend). Drop-in for the
 * old FilterPanel — emits the filtered list via `:filtered-items.sync`. Header rows (isHeader,
 * injected by getFilteredProducts) are ignored for both options and output.
 */
@Component({ components: { MultiSelect } })
export default class ClientFilterPanel extends Vue {
    @Prop({ type: Array, required: true }) items!: Array<any>;
    @Prop({ type: Array, default: () => [] }) filteredItems!: Array<any>;
    @Prop({ type: Boolean, default: false }) sectioned!: boolean;

    private selectedCoverage: string[] = [];
    private selectedValidity: string[] = [];
    private selectedVolume: string[] = [];
    private selectedCategory: string[] = [];

    created() {
        this.emitFiltered();
    }

    @Watch("items")
    itemsChanged() {
        this.emitFiltered();
    }

    private get baseItems(): Array<any> {
        return this.items.filter((i: any) => !i.isHeader);
    }

    private volumeToken(item: any): string {
        return item.packageType === "unlimited" ? "unlimited" : `${item.sizeValue}-${item.sizeUnit}`;
    }

    get coverageItems() {
        const seen = new Set<string>();
        const opts: Array<any> = [];
        this.baseItems.forEach((item: any) => {
            const code = item.countrySet;
            if (code == null || seen.has(code)) return;
            seen.add(code);
            opts.push({ val: code, label: this.$options.filters?.countryName(code) || code, coverageType: coverageTypeOf(item) });
        });
        if (!this.sectioned) {
            return opts.map((o: any) => ({ val: o.val, label: o.label }));
        }
        const sections: Array<{ type: string; title: string }> = [
            { type: "worldwide", title: "Worldwide" },
            { type: "region", title: "Regions" },
            { type: "single", title: "Single Countries" }
        ];
        const items: any[] = [];
        sections.forEach((section: { type: string; title: string }) => {
            const group = opts.filter((o: any) => o.coverageType === section.type);
            if (group.length === 0) return;
            items.push({ val: `__header_${section.type}`, label: section.title, isHeader: true });
            group.forEach((o: any) => items.push({ val: o.val, label: o.label }));
        });
        return items;
    }

    get validityItems() {
        const seen = new Set<string>();
        const opts: Array<any> = [];
        this.baseItems.forEach((item: any) => {
            const val = `${item.validitySize}-${item.validityUnit}`;
            if (seen.has(val)) return;
            seen.add(val);
            opts.push({ val, label: `${item.validitySize} ${item.validityUnit}`, minutes: this.validityMinutes(item.validitySize, item.validityUnit) });
        });
        // Sort by real duration so the dropdown reads 1 day … 365 days regardless of insertion order.
        return opts.sort((a: any, b: any) => a.minutes - b.minutes).map((o: any) => ({ val: o.val, label: o.label }));
    }

    // Validity as an absolute duration in minutes, so mixed units (minutes/days/years) sort correctly.
    private validityMinutes(size: number, unit: string): number {
        const perUnit: { [key: string]: number } = {
            minutes: 1,
            hours: 60,
            days: 1440,
            weeks: 10080,
            months: 43200,
            years: 525600
        };
        return size * (perUnit[String(unit).toLowerCase()] ?? 1);
    }

    get volumeItems() {
        const seen = new Set<string>();
        const opts: Array<any> = [];
        this.baseItems.forEach((item: any) => {
            const val = this.volumeToken(item);
            if (seen.has(val)) return;
            seen.add(val);
            opts.push({ val, label: tools.getVolume(item) });
        });
        return opts;
    }

    get categoryItems() {
        const seen = new Set<string>();
        const opts: Array<any> = [];
        this.baseItems.forEach((item: any) => {
            const val = item.packageCategory;
            if (val == null || seen.has(val)) return;
            seen.add(val);
            const label = val === "default" ? "Basic" : String(val).replace(/^./, (c: string) => c.toUpperCase());
            opts.push({ val, label });
        });
        return opts;
    }

    private onCoverageChange(vals: string[]) {
        this.selectedCoverage = vals;
        this.emitFiltered();
    }

    private onValidityChange(vals: string[]) {
        this.selectedValidity = vals;
        this.emitFiltered();
    }

    private onVolumeChange(vals: string[]) {
        this.selectedVolume = vals;
        this.emitFiltered();
    }

    private onCategoryChange(vals: string[]) {
        this.selectedCategory = vals;
        this.emitFiltered();
    }

    private emitFiltered() {
        const filtered = this.baseItems.filter((item: any) => {
            const coverageOk = !this.selectedCoverage.length || this.selectedCoverage.includes(item.countrySet);
            const validityOk = !this.selectedValidity.length || this.selectedValidity.includes(`${item.validitySize}-${item.validityUnit}`);
            const volumeOk = !this.selectedVolume.length || this.selectedVolume.includes(this.volumeToken(item));
            const categoryOk = !this.selectedCategory.length || this.selectedCategory.includes(item.packageCategory);
            return coverageOk && validityOk && volumeOk && categoryOk;
        });
        this.$emit("update:filteredItems", filtered);
    }
}
